import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import chevronRightIcon from "../../assets/images/chevron-right.svg";
import { PageContext } from "../../context/PageContext";
import { SiteSettingsContext } from "../../context/SiteSettingsContext";
import { buildInternalUrl } from "../../utilities/routes/urlBuilder";
import SanityLink from "../SanityLink";
import { HeaderTheme } from "./HeaderTheme";
import { NavigationItem } from "./NavigationItem";

interface Props {
  items: NavigationItem[];
  open: boolean;
  color: string;
  backButtonLabel?: string;
  onClose: (e?: React.MouseEvent<HTMLElement>) => void;
  updateContainerHeight: (height?: number) => void;
}

interface Theme {
  activeItemBarBgClassName: string;
  activeItemBgClassName: string;
  liBorderClassName: string;
  ulBgClassName: string;
}

const theme: Record<HeaderTheme, Theme> = {
  [HeaderTheme.Primary]: {
    activeItemBarBgClassName: "bg-brand-primary-50",

    // TODO: lighter and lightest color classes don't exist anymore, figure out which classes they should be instead
    activeItemBgClassName: "bg-brand-primary-lighter",
    liBorderClassName: "border-brand-primary-lighter",
    ulBgClassName: "bg-brand-primary-lightest"
  },
  [HeaderTheme.Secondary]: {
    activeItemBarBgClassName: "bg-brand-secondary-50",
    activeItemBgClassName: "bg-brand-secondary-lighter",
    liBorderClassName: "border-brand-secondary-lighter",
    ulBgClassName: "bg-brand-secondary-lightest"
  }
};

export const NavigationNestedSection = ({
  items,
  open,
  backButtonLabel,
  color,
  onClose,
  updateContainerHeight
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const pageContext = useContext(PageContext);
  const { routeInfo } = useContext(SiteSettingsContext);

  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedItemIndex(null);
  }, []);

  useEffect(() => {
    setSelectedItemIndex(null);
  }, [open]);

  useEffect(() => {
    if (!open || !ref?.current) {
      return;
    }

    const targetHeight = ref.current.clientHeight;

    if (!targetHeight) {
      return;
    }

    updateContainerHeight(targetHeight);
  }, [open, updateContainerHeight]);

  const isItemActive = (item: NavigationItem) => {
    if (!item.linkReference) {
      return false;
    }

    const linkUrl = buildInternalUrl(item.linkReference, routeInfo);

    return (
      linkUrl?.length &&
      pageContext.currentPath?.length &&
      linkUrl === pageContext.currentPath
    );
  };

  const onSubLevelExpanded = (e: React.MouseEvent<HTMLElement>, index: number) => {
    setSelectedItemIndex(index);
    e.currentTarget.blur();
  };

  const resetSelection = (e: React.MouseEvent<HTMLElement>) => {
    setSelectedItemIndex(null);
    e.currentTarget.blur();
    e.stopPropagation(); // don't re-open the item

    // reset height after closing sublevel
    if (ref.current) {
      const targetHeight = ref.current.clientHeight;
      if (targetHeight) {
        updateContainerHeight?.(targetHeight);
      }
    }
  };

  const currentTheme = theme[color as HeaderTheme] ?? theme[HeaderTheme.Primary];

  return (
    <div
      className={classNames(
        "absolute top-0 z-10 w-full transition-all duration-300",
        "xl:left-1/2 xl:h-auto",
        open ? "visible" : "invisible",
        selectedItemIndex !== null ? "left-0" : "left-full",
        open ? "z-10 opacity-100" : "z-0 opacity-0 pointer-events-none"
      )}
      ref={ref}
    >
      {!!items?.length && (
        <>
          <ul
            className={classNames(
              "flex flex-col w-full px-5 xl:px-16",
              currentTheme.ulBgClassName
            )}
          >
            <li className="pb-4 xl:hidden">
              <button
                type="button"
                className="flex items-center text-lg cursor-pointer"
                onClick={onClose}
              >
                <svg
                  width={16}
                  height={16}
                  className="fill-current w-2 h-3 mr-2 transform rotate-180"
                >
                  <use xlinkHref={chevronRightIcon.url} />
                </svg>
                <span>{backButtonLabel}</span>
              </button>
            </li>
            {items?.length &&
              items.map((item, index) => {
                if (item.items && item.items.length) {
                  return (
                    <li key={index} className="flex w-full">
                      <button
                        type="button"
                        className={classNames(
                          "flex items-center justify-between xl:justify-start w-full py-4 xl:py-6 xl:px-8 xl:mb-2 last:mb-0 border-b cursor-pointer xl:border-b-0 text-lg xl:text-2xl text-navigation-link xl:rounded-l-full",
                          currentTheme.liBorderClassName,
                          {
                            [currentTheme.activeItemBgClassName]:
                              selectedItemIndex === index
                          }
                        )}
                        onClick={e => onSubLevelExpanded(e, index)}
                      >
                        <span className="mr-5 text-left">{item.title}</span>

                        <svg
                          width={16}
                          height={16}
                          className="fill-current w-2 h-3 xl:w-auto xl:h-auto"
                        >
                          <use xlinkHref={chevronRightIcon.url} />
                        </svg>

                        <NavigationNestedSection
                          items={item.items}
                          open={selectedItemIndex === index}
                          backButtonLabel={item.title}
                          color={color}
                          onClose={resetSelection}
                          updateContainerHeight={updateContainerHeight}
                        />
                      </button>
                    </li>
                  );
                }

                return (
                  <li
                    key={index}
                    className={classNames(
                      "relative py-4 border-b xl:border-b-0",
                      currentTheme.liBorderClassName
                    )}
                  >
                    {isItemActive(item) && (
                      <span
                        className={classNames(
                          "block absolute top-1/2 -left-4 xl:-left-6 w-1 h-4/6 transform -translate-y-1/2",
                          currentTheme.activeItemBarBgClassName
                        )}
                      />
                    )}

                    <SanityLink
                      isExternalLink={item.isExternal}
                      reference={item.linkReference}
                      url={item.externalLink}
                      anchor={item.anchor}
                      className="text-navigation-link hover:text-navigation-link-hover no-underline"
                    >
                      <p className="my-0 uppercase font-bold text-base text-current">
                        {item.title}
                      </p>

                      <small className="text-base text-current">{item.description}</small>
                    </SanityLink>
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </div>
  );
};

